import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const DisciplinesModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state for adding/editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [formData, setFormData] = useState({
    discipline_name: '',
    discipline_code: '',
    display_order: 0,
    is_active: true
  });

  const fetchDisciplines = useCallback(async () => {
    if (!isOpen || !accounts || accounts.length === 0) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/projects/meta/disciplines?active=false'
      );
      setDisciplines(response.data.data || []);
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setError('Failed to load disciplines');
    } finally {
      setLoading(false);
    }
  }, [isOpen, instance, accounts]);

  useEffect(() => {
    fetchDisciplines();
  }, [fetchDisciplines]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      discipline_name: '',
      discipline_code: '',
      display_order: disciplines.length,
      is_active: true
    });
    setIsEditing(false);
    setEditingDiscipline(null);
    setError('');
  };

  const handleEdit = (discipline) => {
    setEditingDiscipline(discipline);
    setFormData({
      discipline_name: discipline.discipline_name,
      discipline_code: discipline.discipline_code,
      display_order: discipline.display_order,
      is_active: discipline.is_active
    });
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.discipline_name.trim() || !formData.discipline_code.trim()) {
      setError('Name and code are required');
      return;
    }

    if (formData.discipline_code.length < 2 || formData.discipline_code.length > 10) {
      setError('Code must be between 2 and 10 characters');
      return;
    }

    try {
      if (editingDiscipline) {
        // Update existing
        await authenticatedApiCall(
          instance,
          accounts,
          'PUT',
          `/projects/meta/disciplines/${editingDiscipline.id}`,
          formData
        );
        setSuccessMessage('Discipline updated successfully');
      } else {
        // Create new
        await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/projects/meta/disciplines',
          formData
        );
        setSuccessMessage('Discipline created successfully');
      }
      
      resetForm();
      fetchDisciplines();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving discipline:', err);
      setError(err.response?.data?.message || 'Failed to save discipline');
    }
  };

  const handleDelete = async (discipline) => {
    if (!window.confirm(`Are you sure you want to delete "${discipline.discipline_name}"? This may affect projects using this discipline.`)) {
      return;
    }

    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/projects/meta/disciplines/${discipline.id}`
      );
      setSuccessMessage('Discipline deleted successfully');
      fetchDisciplines();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting discipline:', err);
      setError(err.response?.data?.message || 'Failed to delete discipline');
    }
  };

  const handleToggleActive = async (discipline) => {
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/projects/meta/disciplines/${discipline.id}`,
        { is_active: !discipline.is_active }
      );
      fetchDisciplines();
    } catch (err) {
      console.error('Error toggling discipline:', err);
      setError('Failed to update discipline status');
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '0',
      width: '90%',
      maxWidth: '700px',
      maxHeight: '85vh',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '1.5rem',
      borderBottom: '1px solid #dee2e6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      margin: 0,
      color: '#333',
      fontSize: '1.25rem'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#666',
      padding: '0.25rem'
    },
    content: {
      padding: '1.5rem',
      overflowY: 'auto',
      flex: 1
    },
    form: {
      backgroundColor: '#f8f9fa',
      padding: '1rem',
      borderRadius: '6px',
      marginBottom: '1.5rem'
    },
    formTitle: {
      margin: '0 0 1rem 0',
      fontSize: '1rem',
      color: '#333'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    },
    formGroup: {
      marginBottom: '0.75rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.25rem',
      fontWeight: '500',
      color: '#555',
      fontSize: '0.9rem'
    },
    input: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.9rem',
      boxSizing: 'border-box'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      fontSize: '0.9rem',
      color: '#555'
    },
    checkbox: {
      marginRight: '0.5rem',
      width: '16px',
      height: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '1rem'
    },
    button: (variant) => ({
      padding: '0.5rem 1rem',
      backgroundColor: variant === 'primary' ? '#007bff' : variant === 'danger' ? '#dc3545' : '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem'
    }),
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '0.75rem',
      textAlign: 'left',
      borderBottom: '2px solid #dee2e6',
      backgroundColor: '#f8f9fa',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#333'
    },
    td: {
      padding: '0.75rem',
      borderBottom: '1px solid #dee2e6',
      fontSize: '0.9rem'
    },
    badge: (isActive) => ({
      padding: '0.25rem 0.5rem',
      backgroundColor: isActive ? '#28a745' : '#6c757d',
      color: 'white',
      borderRadius: '4px',
      fontSize: '0.75rem'
    }),
    codeBadge: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#17a2b8',
      color: 'white',
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontFamily: 'monospace'
    },
    actionButton: {
      padding: '0.25rem 0.5rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.75rem',
      marginRight: '0.25rem'
    },
    alert: (type) => ({
      padding: '0.75rem 1rem',
      backgroundColor: type === 'error' ? '#f8d7da' : '#d4edda',
      color: type === 'error' ? '#721c24' : '#155724',
      borderRadius: '4px',
      marginBottom: '1rem',
      border: `1px solid ${type === 'error' ? '#f5c6cb' : '#c3e6cb'}`
    })
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>📋 Manage Disciplines</h3>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.content}>
          {error && <div style={styles.alert('error')}>{error}</div>}
          {successMessage && <div style={styles.alert('success')}>{successMessage}</div>}

          {/* Add/Edit Form */}
          <div style={styles.form}>
            <h4 style={styles.formTitle}>
              {isEditing ? 'Edit Discipline' : 'Add New Discipline'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name *</label>
                  <input
                    type="text"
                    name="discipline_name"
                    value={formData.discipline_name}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="e.g., Piping"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Code * (2-10 chars)</label>
                  <input
                    type="text"
                    name="discipline_code"
                    value={formData.discipline_code}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="e.g., PIP"
                    maxLength={10}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Display Order</label>
                  <input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleInputChange}
                    style={styles.input}
                    min={0}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      style={styles.checkbox}
                    />
                    Active (available for selection)
                  </label>
                </div>
              </div>
              <div style={styles.buttonGroup}>
                <button type="submit" style={styles.button('primary')}>
                  {isEditing ? 'Update' : 'Add'} Discipline
                </button>
                {isEditing && (
                  <button type="button" onClick={resetForm} style={styles.button('secondary')}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Disciplines List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading disciplines...
            </div>
          ) : disciplines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No disciplines found. Add your first discipline above.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {disciplines
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(discipline => (
                    <tr key={discipline.id}>
                      <td style={styles.td}>{discipline.display_order}</td>
                      <td style={styles.td}>{discipline.discipline_name}</td>
                      <td style={styles.td}>
                        <span style={styles.codeBadge}>{discipline.discipline_code}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(discipline.is_active)}>
                          {discipline.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleEdit(discipline)}
                          style={{ ...styles.actionButton, backgroundColor: '#007bff', color: 'white' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(discipline)}
                          style={{ 
                            ...styles.actionButton, 
                            backgroundColor: discipline.is_active ? '#ffc107' : '#28a745', 
                            color: discipline.is_active ? '#212529' : 'white' 
                          }}
                        >
                          {discipline.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(discipline)}
                          style={{ ...styles.actionButton, backgroundColor: '#dc3545', color: 'white' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisciplinesModal;

